import React, { useEffect, useState, useRef } from 'react';
import cx from 'classnames';
import { useSpring, animated } from '@react-spring/web';
import useMeasure from 'react-use-measure';
import styles from './styles.module.scss';

interface TabPanelProps {
    listOfTabs: { label: string, value: string }[];
    selected: string;
    onChange: (val: string) => void;
    classnames?: string;
}

const TabPanel: React.FC<TabPanelProps> = ({ listOfTabs, selected, onChange, classnames }) => {
    const [boundsRef, bounds] = useMeasure();
    const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

    // Spring animation for the sliding indicator
    const springProps = useSpring({
        to: {
            left: indicatorStyle.left,
            width: indicatorStyle.width,
        },
        config: {
            tension: 300,
            friction: 30,
        },
    });

    useEffect(() => {
        const selectedIndex = listOfTabs.findIndex((tab) => tab.value === selected);
        const selectedTabEl = tabRefs.current[selectedIndex];

        if (selectedTabEl && bounds.left !== undefined) {
            const tabBounds = selectedTabEl.getBoundingClientRect();
            const containerBounds = selectedTabEl.parentElement?.getBoundingClientRect();

            if (containerBounds) {
                setIndicatorStyle({
                    left: tabBounds.left - containerBounds.left,
                    width: tabBounds.width,
                });
            }
        }
    }, [selected, listOfTabs, bounds]);

    return (
        <div ref={boundsRef} className={cx(styles.tabPanelContainer, classnames)}>
            {/* Ghost Indicator */}
            <animated.div
                className={styles.indicator}
                style={springProps}
            />

            {listOfTabs.map((tab, index) => (
                <div
                    key={tab.value}
                    ref={(el) => (tabRefs.current[index] = el)}
                    className={cx(styles.tabItem, {
                        [styles.active]: selected === tab.value,
                    })}
                    onClick={() => onChange(tab.value)}
                >
                    {tab.label}
                </div>
            ))}
        </div>
    );
};

export default TabPanel;
